import { useCallback } from 'react';
import { readAccessUserId } from '../utils/auth';

// Cache durations based on user requirements
const CACHE_DURATION = {
  analytics: 15 * 60 * 1000,      // 15 minutes
  events: 24 * 60 * 60 * 1000,    // 1 day
  news: 24 * 60 * 60 * 1000,      // 1 day (generated once per day)
  preferences: 24 * 60 * 60 * 1000 // 1 day
};

// Daily refresh limits
const MAX_DAILY_REFRESHES = {
  events: 1,
  news: 1
};

const useInsightsCache = () => {
  // Removed unused refreshCounts state

  // Get today's date key for daily tracking
  const getTodayKey = () => {
    return new Date().toISOString().split('T')[0]; // YYYY-MM-DD
  };

  // Check if we're still on the same day
  const isSameDay = useCallback((timestamp) => {
    const today = getTodayKey();
    const cachedDate = new Date(timestamp).toISOString().split('T')[0];
    return today === cachedDate;
  }, []);

  const storageKey = useCallback((key) => {
    const userId = readAccessUserId();
    if (!userId) return null;
    return `insights_${key}:${userId}`;
  }, []);

  // Drop the unscoped key and this account's copy. Another account's key stays
  // until sign-out clears every insights_* entry.
  const clearCachedData = useCallback((key) => {
    try {
      localStorage.removeItem(`insights_${key}`);
      const scoped = storageKey(key);
      if (scoped) localStorage.removeItem(scoped);
    } catch {
      /* storage can be unavailable */
    }
  }, [storageKey]);

  // Get cached data with expiration check. Never reads another account's copy
  // or the legacy unscoped key.
  const getCachedData = useCallback((key, maxAge) => {
    try {
      localStorage.removeItem(`insights_${key}`);
      const scoped = storageKey(key);
      if (!scoped) return null;
      const cached = localStorage.getItem(scoped);
      if (!cached) return null;
      
      const { data, timestamp } = JSON.parse(cached);
      const age = Date.now() - timestamp;
      
      // For news, check if it's from today
      if (key === 'news') {
        if (!isSameDay(timestamp)) {
          localStorage.removeItem(scoped);
          return null;
        }
        return data;
      }
      
      // For other data types, use maxAge
      if (age > maxAge) {
        localStorage.removeItem(scoped);
        return null;
      }
      
      return data;
    } catch (error) {
      console.error('Error reading cached data:', error);
      clearCachedData(key);
      return null;
    }
  }, [isSameDay, storageKey, clearCachedData]);

  // Set cached data with timestamp
  const setCachedData = useCallback((key, data) => {
    try {
      const scoped = storageKey(key);
      if (!scoped) return;
      const cacheData = {
        data,
        timestamp: Date.now()
      };
      localStorage.setItem(scoped, JSON.stringify(cacheData));
    } catch (error) {
      console.error('Error caching data:', error);
    }
  }, [storageKey]);

  // Get daily refresh count for a specific data type
  const getDailyRefreshCount = useCallback((dataType) => {
    const today = getTodayKey();
    const countKey = `refresh_count_${dataType}_${today}`;
    
    try {
      const stored = localStorage.getItem(countKey);
      return stored ? parseInt(stored, 10) : 0;
    } catch (error) {
      return 0;
    }
  }, []);

  // Increment daily refresh count
  const incrementRefreshCount = useCallback((dataType) => {
    const today = getTodayKey();
    const countKey = `refresh_count_${dataType}_${today}`;
    const currentCount = getDailyRefreshCount(dataType);
    
    try {
      localStorage.setItem(countKey, (currentCount + 1).toString());
      // Removed setRefreshCounts as it's no longer used
    } catch (error) {
      console.error('Error updating refresh count:', error);
    }
  }, [getDailyRefreshCount]);

  // Check if refresh is allowed for a data type
  const canRefresh = useCallback((dataType) => {
    const maxRefreshes = MAX_DAILY_REFRESHES[dataType];
    if (!maxRefreshes) return true; // No limit set
    
    const currentCount = getDailyRefreshCount(dataType);
    return currentCount < maxRefreshes;
  }, [getDailyRefreshCount]);

  // Get remaining refreshes for today
  const getRemainingRefreshes = useCallback((dataType) => {
    const maxRefreshes = MAX_DAILY_REFRESHES[dataType];
    if (!maxRefreshes) return Infinity;
    
    const currentCount = getDailyRefreshCount(dataType);
    return Math.max(0, maxRefreshes - currentCount);
  }, [getDailyRefreshCount]);

  // Clear expired cache entries (cleanup utility)
  const clearExpiredCache = useCallback(() => {
    const keys = Object.keys(localStorage);
    const now = Date.now();
    
    keys.forEach(key => {
      if (key.startsWith('insights_')) {
        try {
          const cached = localStorage.getItem(key);
          if (cached) {
            const { timestamp } = JSON.parse(cached);
            const dataType = key.slice('insights_'.length).split(':')[0];
            const maxAge = CACHE_DURATION[dataType];
            
            if (maxAge && (now - timestamp > maxAge)) {
              localStorage.removeItem(key);
            }
          }
        } catch (error) {
          // Remove corrupted cache entries
          localStorage.removeItem(key);
        }
      }
      
      // Clean up old refresh counts (older than 7 days)
      if (key.startsWith('refresh_count_')) {
        const parts = key.split('_');
        const dateStr = parts[parts.length - 1];
        const cacheDate = new Date(dateStr);
        const daysDiff = (now - cacheDate.getTime()) / (1000 * 60 * 60 * 24);
        
        if (daysDiff > 7) {
          localStorage.removeItem(key);
        }
      }
    });
  }, []);

  // Check if data needs to be fetched
  const needsFetch = useCallback((dataType) => {
    const maxAge = CACHE_DURATION[dataType];
    const cached = getCachedData(dataType, maxAge);
    return !cached;
  }, [getCachedData]);

  return {
    // Core caching functions
    getCachedData,
    setCachedData,
    clearCachedData,
    needsFetch,
    
    // Refresh management
    canRefresh,
    incrementRefreshCount,
    getRemainingRefreshes,
    
    // Utilities
    clearExpiredCache,
    
    // Constants for external use
    CACHE_DURATION,
    MAX_DAILY_REFRESHES
  };
};

export default useInsightsCache; 